import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedAuctionListingDetails,
  type OrchestratedOfferingListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { Tag, TagType, FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { useSellerCollectsListingTagAnalytics } from '@/buyer-portal/helpers/sellerCollectsAnalytics'
import { getListingSaleKind } from '@/buyer-portal/helpers/sellerCollectsSaleKind'
import {
  CreditCardTag,
  CreditCardNotAcceptedTag,
} from '@/components/listings/CreditCardTag'
import { SellerCollectsTag } from '@/components/listings/SellerCollectsTag'
import { FeatureFlags } from '@/helpers/featureFlags'

import { useListingTagsModel } from './useListingTagsModel'

const ns = 'Listing.tags'

const TagContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: nowrap;
`

export const ListingTags = ({
  listing,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
}) => {
  const {
    listingType,
    pricingStrategy,
    isBuyNow,
    showListingTypeTag,
    showPricingStrategyTag,
    isSellerCollects,
    sellerName,
    hasBuyerPremium,
    sellerArrangesShipping,
    isBuyerPremiumSettled,
  } = useListingTagsModel(listing)
  const ccSurfacingEnabled = useFlag(FeatureFlags.ENABLE_CC_SURFACING)
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )
  const showSellerCollectsTag = sellerCollectsEnabled && isSellerCollects
  // The listing view is reported from here rather than the page: this row is
  // rendered once per listing page and already holds the seller's fee model.
  const { onExplainerOpened } = useSellerCollectsListingTagAnalytics(listing, {
    isSellerCollects,
    hasBuyerPremium,
    isBuyerPremiumSettled,
  })
  const showCreditCardTag =
    ccSurfacingEnabled && listing.isCreditCardEnabled === true
  const showCreditCardNotAcceptedTag =
    ccSurfacingEnabled && listing.isCreditCardEnabled === false

  return (
    <TagContainer>
      {showListingTypeTag ? (
        <Tag
          type={listingType === 'FULFILLMENT' ? TagType.info : TagType.normal}
        >
          <FormattedMessage id={`${ns}.listingType.${listingType}`} />
        </Tag>
      ) : null}
      {showPricingStrategyTag ? (
        <Tag type={TagType.info}>
          <FormattedMessage id={`${ns}.pricingStrategy.${pricingStrategy}`} />
        </Tag>
      ) : null}
      {pricingStrategy === 'AUCTION' && isBuyNow ? (
        <Tag type={TagType.info}>
          <FormattedMessage id={`${ns}.buyNow`} />
        </Tag>
      ) : null}
      {showSellerCollectsTag && (
        <SellerCollectsTag
          sellerName={sellerName}
          hasBuyerPremium={hasBuyerPremium}
          sellerArrangesShipping={sellerArrangesShipping}
          onExplainerOpened={onExplainerOpened}
          saleKind={getListingSaleKind(listing)}
        />
      )}
      {showCreditCardTag && <CreditCardTag />}
      {showCreditCardNotAcceptedTag && <CreditCardNotAcceptedTag />}
    </TagContainer>
  )
}
